// Type-only import: the runtime side of the card pulls in icon modules, which unit tests
// running this pure mapping have no reason to load.
import { isSentinelQueuedXchainWithdraw } from "./transfer-arms";
import type { TransferProgressData, TransferStatus, TransferStep } from "./transfer-progress-card";

export interface TimelineRow {
  /** Drives `key`, `data-testid="transfer-step-<step>"`, the title/subtitle switches and the icon. */
  step: TransferStep;
  /** Drives `data-step-status`, the title/subtitle "has finished" branches, and `completedSteps`. */
  status: TransferStatus;
  /** The card's last row: the connector line is omitted and the icon reads complete once reached. */
  isLast: boolean;
}

const APTOS_NATIVE_STEPS: TransferStep[] = ["initiated", "confirming", "available"];

function timelineSteps(transfer: TransferProgressData): TransferStep[] {
  // Intentionally state-dependent, not a static list. Slot 3 is `claimable` only while the card
  // sits there and `bridging` otherwise; slot 4 is `completed` only at the end and `available`
  // otherwise. This collapse is what makes a CCTP card four rows instead of five — "Step 2 of 4",
  // and four `transfer-step-*` nodes for `packages/e2e/src/ui/utils/xchain/bridge-poller.ts` to
  // scan. Expanding it is a user-visible copy change and an E2E change; do not "fix" it.
  const CCTP_BRIDGE_STEPS: TransferStep[] = [
    "initiated",
    "confirming",
    transfer.currentStep === "claimable" ? "claimable" : "bridging",
    transfer.currentStep === "completed" ? "completed" : "available",
  ];

  switch (transfer.type) {
    case "cctp-deposit":
    case "cctp-withdraw":
      return CCTP_BRIDGE_STEPS;

    case "queued-withdraw":
      if (transfer.currentStep === "withdrawal-cancelled") {
        return ["initiated", "withdrawal-queued", "withdrawal-cancelled"];
      }
      return ["initiated", "withdrawal-queued", "available"];

    case "queued-xchain-withdraw": {
      // Cancellation is checked ahead of the sentinel: a cancelled sentinel is still a cancelled
      // withdrawal, and its last row has to say so.
      if (transfer.currentStep === "withdrawal-cancelled") {
        return ["initiated", "withdrawal-queued", "withdrawal-cancelled"];
      }
      // Sentinel from auto-import: destination chain unknown, show simple steps. The same
      // predicate the card's subtitle and `selectNextAction` use — the three must never disagree
      // about which cards these are.
      if (isSentinelQueuedXchainWithdraw(transfer)) {
        return ["initiated", "withdrawal-queued", "available"];
      }
      return ["initiated", "withdrawal-queued", ...CCTP_BRIDGE_STEPS.slice(1)];
    }

    case "mesh-deposit":
      // @Todo: Handle mesh deposit steps for non-Aptos chains, once we implement it
      return APTOS_NATIVE_STEPS;

    case "xchain-deposit":
      // A failed swap replaces the final step rather than appending to it: nothing was delivered,
      // so "available" never happens. It does not mean the funds came back — ANY_INPUT has no
      // refund path and retries instead, which is why the card reports a status rather than a
      // reason.
      if (transfer.currentStep === "transfer-failed") {
        return ["initiated", "confirming", "transfer-failed"];
      }
      return APTOS_NATIVE_STEPS;

    case "xchain-withdraw":
      // A refund lands back in the user's own Aptos wallet, so as on the deposit side the failure
      // replaces the final step rather than following it.
      if (transfer.currentStep === "transfer-failed") {
        return ["initiated", "confirming", "transfer-failed"];
      }
      // Ends at `completed`, not `available`: the funds left the exchange, so there is no
      // follow-up move to offer and delivery is the last thing that happens.
      return ["initiated", "confirming", "bridging", "completed"];

    default:
      // Dead for the seven arms above, kept as the guard for a legacy persisted `type`.
      return APTOS_NATIVE_STEPS;
  }
}

/**
 * The rows a card renders, in order, each with the status it displays.
 *
 * A pure function of the record: same input, same rows, with no memoisation and no assumption that
 * a card keeps its identity — `replaceTransfer` swaps one for another of a different type.
 */
export function describeTimeline(transfer: TransferProgressData): TimelineRow[] {
  const steps = timelineSteps(transfer);

  // `indexOf` rather than the row's own position, character-for-character as the card computed it.
  // The two agree because no row list repeats a step, which `key={row.step}` already required.
  const currentIndex = steps.indexOf(transfer.currentStep);

  // A step the row list does not contain reads -1, and every row then comes out `pending` — a
  // legacy cross-chain deposit persisted at `completed` renders three grey rows under a title that
  // says it is done. `getProcessedTargetStep` (`@/utils/withdraw-queue-events`) is what keeps the
  // auto-import sentinel out of this state; nothing rescues a card already persisted in it.
  const statusOf = (step: TransferStep): TransferStatus => {
    const stepIndex = steps.indexOf(step);
    // Last step is complete when it's the current step or beyond
    if (stepIndex === steps.length - 1 && currentIndex >= stepIndex) {
      return "complete";
    }
    if (currentIndex > stepIndex) return "complete";
    if (currentIndex === stepIndex) return "in-progress";
    return "pending";
  };

  return steps.map((step, index) => ({
    step,
    status: statusOf(step),
    isLast: index === steps.length - 1,
  }));
}
